import React, { useState, useEffect, useCallback, useRef } from "react";
import { Sparkles, Users, Eye, Check, HelpCircle, X, Phone, Upload, UserPlus, Send, Copy, Loader2, TrendingUp, Wallet } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MyInvitesDashboard — customer's invites list + per-invite guest management.
 * Shows: RSVP tallies, referral credits earned, contact picker, guest list + interest scores,
 * one-click "Re-engage interested guests" + auto-post-booking re-engagement (backend-driven).
 */
export default function MyInvitesDashboard() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState({ total_amount: 0, count: 0 });
  const [openInviteId, setOpenInviteId] = useState(null);

  const fetchInvites = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/smart-invites/mine`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setInvites(d.invites || []);
      }
    } catch { /* ignore */ }
    try {
      const c = await fetch(`${API_URL}/api/smart-invites/my-credits`, { credentials: "include" });
      if (c.ok) setCredits(await c.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  if (loading) {
    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-[#f0e6d9] p-5" data-testid="my-invites-loading">
        <Loader2 size={20} className="text-[#E83E8C] animate-spin mx-auto" />
      </div>
    );
  }

  if (invites.length === 0) return null; // Don't show section until first invite exists

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-[#f0e6d9] p-5" data-testid="my-invites-dashboard">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-[#1E1B4B] uppercase tracking-wider flex items-center gap-2">
          <Sparkles size={16} className="text-[#E83E8C]" /> My Smart Invites
        </h3>
        {credits.total_amount > 0 && (
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-200 text-emerald-700" data-testid="referral-credits-pill">
            <Wallet size={14} />
            <span className="text-xs font-bold">₹{credits.total_amount.toLocaleString()} party credits earned</span>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {invites.map((inv) => (
          <InviteCard
            key={inv.invite_id}
            invite={inv}
            expanded={openInviteId === inv.invite_id}
            onToggle={() => setOpenInviteId(openInviteId === inv.invite_id ? null : inv.invite_id)}
            onRefresh={fetchInvites}
          />
        ))}
      </div>
    </div>
  );
}

function InviteCard({ invite, expanded, onToggle, onRefresh }) {
  const counts = invite.rsvp_counts || { attending: 0, maybe: 0, no: 0 };
  const total = counts.attending + counts.maybe + counts.no;

  return (
    <div className="rounded-xl border border-[#f0e6d9] bg-[#FFFAF5] overflow-hidden" data-testid={`invite-card-${invite.invite_id}`}>
      <button
        onClick={onToggle}
        className="w-full p-4 text-left hover:bg-[#FFFAF5]/70 transition-colors"
        data-testid={`invite-toggle-${invite.invite_id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-[#1E1B4B] text-sm truncate">
              {invite.owner_name}'s {invite.occasion}
            </p>
            <p className="text-[11px] text-[#94a3b8] mt-0.5">
              {invite.event_date}{invite.event_time ? ` · ${invite.event_time}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 text-[11px]">
            <span className="flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold"><Check size={10} /> {counts.attending}</span>
            <span className="flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold"><HelpCircle size={10} /> {counts.maybe}</span>
            <span className="flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-semibold"><Eye size={10} /> {invite.opens || 0}</span>
          </div>
        </div>
        {total > 0 && (
          <div className="mt-2 flex h-1.5 rounded-full overflow-hidden bg-gray-200">
            <div className="bg-emerald-500" style={{ width: `${(counts.attending / total) * 100}%` }} />
            <div className="bg-amber-500" style={{ width: `${(counts.maybe / total) * 100}%` }} />
            <div className="bg-rose-500" style={{ width: `${(counts.no / total) * 100}%` }} />
          </div>
        )}
      </button>

      {expanded && (
        <InviteDetails inviteId={invite.invite_id} onRefresh={onRefresh} />
      )}
    </div>
  );
}

function InviteDetails({ inviteId, onRefresh }) {
  const [data, setData] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reengaging, setReengaging] = useState(false);
  const [reengageResult, setReengageResult] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, c] = await Promise.all([
        fetch(`${API_URL}/api/smart-invites/${inviteId}/guests`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/api/smart-invites/${inviteId}/contacts`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
      ]);
      if (g) setData(g);
      if (c) setContacts(c.contacts || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [inviteId]);

  useEffect(() => { load(); }, [load]);

  const reengage = async () => {
    setReengaging(true); setReengageResult("");
    try {
      const r = await fetch(`${API_URL}/api/smart-invites/${inviteId}/reengage`, {
        method: "POST", credentials: "include"
      });
      const d = await r.json();
      setReengageResult(r.ok ? `Sent follow-up to ${d.sent || 0} interested guest${d.sent === 1 ? "" : "s"}.` : "Couldn't send follow-ups.");
      onRefresh && onRefresh();
    } catch { setReengageResult("Network error."); }
    setReengaging(false);
  };

  const shareUrl = `${window.location.origin}/rsvp/${inviteId}`;
  const copyLink = () => { navigator.clipboard.writeText(shareUrl).catch(() => {}); };

  if (loading) return <div className="p-5 text-center"><Loader2 size={18} className="animate-spin mx-auto text-[#E83E8C]" /></div>;
  if (!data) return null;

  const rsvps = data.rsvps || [];
  const interested = rsvps.filter(r => r.status === "attending" || r.status === "maybe");

  return (
    <div className="border-t border-[#f0e6d9] p-4 space-y-4" data-testid="invite-details-panel">
      {/* Actions row */}
      <div className="flex flex-wrap gap-2">
        <button onClick={copyLink} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E83E8C]/10 text-[#E83E8C] text-xs font-semibold hover:bg-[#E83E8C]/20 transition-colors" data-testid="invite-copy-link">
          <Copy size={12} /> Copy invite link
        </button>
        <button
          onClick={reengage}
          disabled={reengaging || interested.length === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#25D366] to-[#1da851] text-white text-xs font-semibold hover:opacity-90 transition-all disabled:opacity-40"
          data-testid="invite-reengage-btn"
          title={interested.length === 0 ? "No interested guests yet" : "Send a promo offer to interested guests"}
        >
          <Send size={12} /> Re-engage interested ({interested.length})
        </button>
      </div>
      {reengageResult && <p className="text-xs text-[#475569]">{reengageResult}</p>}
      {data.campaigns_sent > 0 && (
        <p className="text-[10px] text-[#94a3b8] flex items-center gap-1">
          <TrendingUp size={10} /> {data.campaigns_sent} follow-up message(s) sent for this invite
        </p>
      )}

      <ContactsSection inviteId={inviteId} contacts={contacts} onRefresh={() => { load(); onRefresh && onRefresh(); }} />

      {rsvps.length > 0 && (
        <div>
          <p className="text-[11px] font-bold text-[#94a3b8] uppercase tracking-wider mb-2 flex items-center gap-1">
            <Users size={11} /> Guest Responses ({rsvps.length})
          </p>
          <div className="space-y-1.5">
            {rsvps.map(g => {
              const pill = {
                attending: { cls: "bg-emerald-100 text-emerald-700", label: "Attending" },
                maybe:     { cls: "bg-amber-100 text-amber-700", label: "Maybe" },
                no:        { cls: "bg-rose-100 text-rose-700", label: "Can't make it" },
              }[g.status] || { cls: "bg-gray-100 text-gray-600", label: g.status };
              return (
                <div key={g.rsvp_id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-white border border-[#f0e6d9] text-xs" data-testid={`guest-row-${g.rsvp_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-[#1E1B4B] truncate">{g.guest_name || "Anonymous guest"}</p>
                    {g.message && <p className="text-[#94a3b8] italic truncate">"{g.message}"</p>}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${pill.cls}`}>
                    {pill.label}
                  </span>
                  {g.interest_score > 30 && (
                    <span className="text-[9px] font-bold text-[#FF9F1C]" title="Interest score">
                      🔥 {g.interest_score}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ContactsSection({ inviteId, contacts, onRefresh }) {
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);

  const pickContacts = async () => {
    setMsg("");
    // Web Contacts Picker API (Chrome on Android)
    if (!("contacts" in navigator) || !navigator.contacts || typeof navigator.contacts.select !== "function") {
      setMsg("Your browser doesn't support the phone contacts picker. Use CSV/vCard upload instead.");
      return;
    }
    setAdding(true);
    try {
      const picked = await navigator.contacts.select(["name", "tel"], { multiple: true });
      const items = picked
        .map(p => ({ name: (p.name && p.name[0]) || "", phone: (p.tel && p.tel[0]) || "" }))
        .filter(x => x.phone);
      if (!items.length) { setMsg("No contacts selected."); setAdding(false); return; }
      const r = await fetch(`${API_URL}/api/smart-invites/${inviteId}/contacts`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contacts: items, source: "picker" }),
      });
      const d = await r.json();
      setMsg(`Added ${d.added || 0} contacts${d.skipped ? ` (${d.skipped} skipped)` : ""}.`);
      onRefresh && onRefresh();
    } catch (e) {
      setMsg(e?.message?.includes("cancel") ? "" : "Couldn't pick contacts.");
    }
    setAdding(false);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAdding(true); setMsg("");
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await fetch(`${API_URL}/api/smart-invites/${inviteId}/contacts/upload`, {
        method: "POST", credentials: "include", body: form,
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setMsg(d.detail || "Upload failed.");
      } else {
        const d = await r.json();
        setMsg(`Added ${d.added || 0} contacts${d.skipped ? ` (${d.skipped} duplicates skipped)` : ""}.`);
        onRefresh && onRefresh();
      }
    } catch { setMsg("Upload failed."); }
    setAdding(false);
    e.target.value = "";
  };

  return (
    <div data-testid="invite-contacts-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold text-[#94a3b8] uppercase tracking-wider flex items-center gap-1">
          <Phone size={11} /> Guest Contacts ({contacts.length})
        </p>
        <div className="flex gap-2">
          <button
            onClick={pickContacts}
            disabled={adding}
            className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#8b5cf6]/10 text-[#8b5cf6] text-[10px] font-semibold hover:bg-[#8b5cf6]/20 disabled:opacity-50"
            data-testid="pick-contacts-btn"
          >
            <UserPlus size={10} /> Pick from Phone
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={adding}
            className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#FF9F1C]/10 text-[#FF9F1C] text-[10px] font-semibold hover:bg-[#FF9F1C]/20 disabled:opacity-50"
            data-testid="upload-contacts-btn"
          >
            <Upload size={10} /> Upload .vcf/.csv
          </button>
          <input ref={fileRef} type="file" accept=".vcf,.csv,text/vcard,text/csv" onChange={uploadFile} className="hidden" data-testid="contacts-file-input" />
        </div>
      </div>
      {msg && <p className="text-[10px] text-[#475569] mb-1">{msg}</p>}
      {adding && <div className="flex items-center gap-1 text-[10px] text-[#475569]"><Loader2 size={10} className="animate-spin" /> Adding…</div>}
      {contacts.length > 0 && (
        <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
          {contacts.slice(0, 30).map(c => (
            <span key={c.phone} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-[#f0e6d9] text-[10px] text-[#1E1B4B]" data-testid={`contact-${c.phone}`}>
              {c.name || c.phone}
            </span>
          ))}
          {contacts.length > 30 && <span className="text-[10px] text-[#94a3b8]">+{contacts.length - 30} more</span>}
        </div>
      )}
    </div>
  );
}
